import "../Matches/PortalSurfaces.css";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import { getCurrentUser } from "aws-amplify/auth";
import { Plus } from "lucide-react";
import { isTournamentV2Enabled } from "../../lib/competitionApiEnvironment";
import {
  listMyTournamentParticipants,
  listTournamentsPage
} from "../../lib/leaderboardApi";
import { buildAuthReturnPath, buildCurrentReturnTo } from "../../lib/authReturn";
import { CompetitionAuthGate } from "../Competition/CompetitionRoutePanels";
import { formatCalendarDate, parseCalendarDate } from "../../utils/date";
import "../Leaderboard/Leaderboard.css";

const MAX_OWNED_TOURNAMENT_PAGES = 10;

function familyLabel(value) {
  if (value === "arc") return "NRA ARC";
  if (value === "bullseye") return "Bullseye";
  if (value === "uspsa") return "USPSA";
  return "Tournament";
}

function statusLabel(value) {
  const label = String(value || "draft").replace(/_/g, " ");
  return label.replace(/^./, (char) => char.toUpperCase());
}

function formatDate(value) {
  if (!value) return "Date not set";
  return formatCalendarDate(value);
}

function formatDateRange(tournament) {
  const start = formatDate(tournament?.startDate);
  const end = formatDate(tournament?.endDate);
  if (start === "Date not set" && end === "Date not set") return "Date not set";
  if (start === end || end === "Date not set") return start;
  return `${start} - ${end}`;
}

function tournamentTimeValue(tournament) {
  const value = parseCalendarDate(tournament?.startDate || tournament?.createdAt || 0)?.getTime();
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

function isPublicTournament(tournament) {
  return String(tournament?.visibility || "public").toLowerCase() === "public";
}

function isActiveTournament(tournament) {
  const status = String(tournament?.status || "").toLowerCase();
  return status === "active" || status === "draft";
}

function participantDisplayName(row) {
  return (
    row?.tournamentName ||
    row?.name ||
    row?.tournamentDisplayName ||
    row?.displayName ||
    "Tournament access"
  );
}

function normalizeParticipant(row) {
  const status = String(row?.participantStatus || "pending").toLowerCase();
  return {
    ...row,
    status,
    tournamentId: String(row?.tournamentId || "").trim(),
    displayName: participantDisplayName(row),
    family: row?.competitionFamily || row?.tournamentFamily || "",
    canLinkNativeMatches: row?.canLinkNativeMatches !== false
  };
}

function isAuthMessage(value) {
  return /authenticated|not authenticated|sign in|no current user|token/i.test(String(value || ""));
}

function friendlyErrorMessage(error, fallback) {
  const message = error?.message || error;
  if (isAuthMessage(message)) {
    return "Sign in to review tournament admin access.";
  }
  return message || fallback;
}

async function loadOwnedTournamentPages() {
  const tournaments = [];
  let nextToken = null;
  let pageCount = 0;

  do {
    const page = await listTournamentsPage({
      owned: true,
      visibility: null,
      status: "all",
      limit: 50,
      sortDir: "asc",
      nextToken
    });
    tournaments.push(...(page?.data || []));
    nextToken = page?.nextToken || null;
    pageCount += 1;
  } while (nextToken && pageCount < MAX_OWNED_TOURNAMENT_PAGES);

  return {
    data: tournaments,
    partial: Boolean(nextToken)
  };
}

function tournamentWorkbenchPath(tournamentId) {
  return `/matches/manage/tournaments/${encodeURIComponent(tournamentId || "")}`;
}

function tournamentPublicPath(tournamentId) {
  return `/tournaments/${encodeURIComponent(tournamentId || "")}`;
}

function DirectorTournaments() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [ownedTournaments, setOwnedTournaments] = useState([]);
  const [participantRows, setParticipantRows] = useState([]);
  const [loadErrors, setLoadErrors] = useState([]);

  useEffect(() => {
    let active = true;

    async function loadTournamentAccess() {
      setLoading(true);
      try {
        await getCurrentUser();
        if (!active) return;
        setSignedIn(true);
        setLoadErrors([]);

        const [ownedResult, participantResult] = await Promise.allSettled([
          loadOwnedTournamentPages(),
          listMyTournamentParticipants()
        ]);
        if (!active) return;

        const nextErrors = [];
        let authFailure = false;

        if (ownedResult.status === "fulfilled") {
          setOwnedTournaments(
            (ownedResult.value.data || [])
              .slice()
              .sort((left, right) => tournamentTimeValue(left) - tournamentTimeValue(right))
          );
          if (ownedResult.value.partial) {
            nextErrors.push("Owned tournaments returned more pages than this preview shows");
          }
        } else {
          authFailure = authFailure || isAuthMessage(ownedResult.reason?.message);
          nextErrors.push(friendlyErrorMessage(ownedResult.reason, "Owned tournaments could not be loaded."));
          setOwnedTournaments([]);
        }

        if (participantResult.status === "fulfilled") {
          setParticipantRows((participantResult.value || []).map(normalizeParticipant));
        } else {
          authFailure = authFailure || isAuthMessage(participantResult.reason?.message);
          nextErrors.push(friendlyErrorMessage(participantResult.reason, "Organizer access could not be loaded."));
          setParticipantRows([]);
        }

        if (authFailure) {
          setSignedIn(false);
          setLoadErrors([]);
          return;
        }

        setLoadErrors(nextErrors);
      } catch (authError) {
        if (!active) return;
        const message = String(authError?.message || "");
        setSignedIn(false);
        setOwnedTournaments([]);
        setParticipantRows([]);
        setLoadErrors(
          message && !isAuthMessage(message)
            ? [friendlyErrorMessage(authError, "Unable to load tournament admin access.")]
            : []
        );
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadTournamentAccess();
    return () => {
      active = false;
    };
  }, []);

  const ownedIds = useMemo(
    () => new Set(ownedTournaments.map((tournament) => String(tournament.tournamentId || ""))),
    [ownedTournaments]
  );
  const organizerRows = useMemo(
    () => participantRows.filter((row) => !ownedIds.has(row.tournamentId)),
    [ownedIds, participantRows]
  );
  const approvedRows = organizerRows.filter((row) => row.status === "approved");
  const pendingRows = organizerRows.filter((row) => row.status === "pending");
  const publicOwned = ownedTournaments.filter(isPublicTournament);
  const privateOwned = ownedTournaments.filter((tournament) => !isPublicTournament(tournament));
  const activeOwned = ownedTournaments.filter(isActiveTournament);
  const emptyAccess = ownedTournaments.length === 0 && organizerRows.length === 0;
  const signInPath = buildAuthReturnPath("/auth/sign-in", buildCurrentReturnTo(location, "/matches/manage/tournaments"));

  return (
    <section className="leaderboard-page portal-surface">
      <Helmet>
        <title>Tournament Admin | Range Pocket Competition</title>
      </Helmet>

      <div className="match-board-hero">
        <div>
          <h1>Tournament Admin</h1>
          <p className="competition-page-lede">
            Manage tournament workbenches, attached matches, organizer access,
            and public leaderboard displays.
          </p>
        </div>
        {signedIn && isTournamentV2Enabled() && <Link to="/matches/manage/tournaments/new" className="match-search-button leaderboard-inline-link"><Plus size={18} aria-hidden="true" /> New tournament</Link>}
      </div>

      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel">
          <div className="owner-registration-section-label">Loading</div>
          <strong>Loading tournament access</strong>
          <span>Checking owned and organizer workspaces</span>
        </div>
      )}

      {!loading && !signedIn && (
        <CompetitionAuthGate
          eyebrow="Director access"
          title="Sign in to review tournaments"
          signInPath={signInPath}
          links={[
            { to: "/matches/manage", label: "Director Console" },
            { to: "/tournaments", label: "Public Tournaments" },
          ]}
        >
          Tournament admin shows existing tournament workbenches, organizer access, and ARC match attachments tied to your account.
          {loadErrors.length > 0 && <p className="leaderboard-error-inline">{loadErrors.join(" ")}</p>}
        </CompetitionAuthGate>
      )}

      {!loading && signedIn && (
        <div className="leaderboard-stack">
          <section className="leaderboard-card director-console-summary director-tournament-summary">
            <div className="director-summary-item">
              <span>Owned</span>
              <strong>{ownedTournaments.length}</strong>
            </div>
            <div className="director-summary-item">
              <span>Active / Draft</span>
              <strong>{activeOwned.length}</strong>
            </div>
            <div className="director-summary-item">
              <span>Approved Access</span>
              <strong>{approvedRows.length}</strong>
            </div>
            <div className="director-summary-item">
              <span>Pending</span>
              <strong>{pendingRows.length}</strong>
            </div>
          </section>

          {loadErrors.length > 0 && (
            <div className="leaderboard-card leaderboard-message-card">
              <span>Showing partial tournament access. {loadErrors.join(" ")}</span>
            </div>
          )}

          <section className="director-tournament-guidance">
            <div className="director-tournament-guidance-actions">
              <Link to="/matches/manage/matches" className="registration-auth-secondary leaderboard-inline-link">
                Owned Matches
              </Link>
              <Link to="/matches/manage/displays" className="registration-auth-secondary leaderboard-inline-link">
                Displays
              </Link>
              <Link to="/tournaments" className="registration-auth-secondary leaderboard-inline-link">
                Public Tournaments
              </Link>
            </div>
          </section>

          {emptyAccess && (
            <section className="leaderboard-card director-tournament-empty list-empty-panel">
              <div>
                <div className="owner-registration-section-label">No tournament admin access</div>
                <h2>No tournament access yet</h2>
                <div className="empty-filter-summary" aria-label="Tournament access state">
                  <span>Owner: 0</span>
                  <span>Organizer: 0</span>
                </div>
              </div>
              <div className="director-action-row">
                <Link to="/matches/manage" className="match-search-button leaderboard-inline-link">
                  Director Overview
                </Link>
                <Link to="/matches/manage/matches" className="registration-auth-secondary leaderboard-inline-link">
                  Owned Matches
                </Link>
              </div>
            </section>
          )}

          {ownedTournaments.length > 0 && (
            <section className="leaderboard-card tournament-management-section">
              <div className="tournament-section-header">
                <div>
                  <div className="owner-registration-section-label">Owner</div>
                  <h2>Owned tournaments</h2>
                </div>
                <div className="leaderboard-pill-row">
                  <span className="open-match-chip">{publicOwned.length} public</span>
                  <span className="open-match-chip">{privateOwned.length} private</span>
                </div>
              </div>

              <div className="director-tournament-list">
                {ownedTournaments.map((tournament) => (
                  <article key={tournament.tournamentId} className="director-tournament-row leaderboard-link-card">
                    <Link
                      to={tournamentWorkbenchPath(tournament.tournamentId)}
                      className="card-click-target"
                      aria-label={`Manage ${tournament.name || "this tournament"}`}
                    />
                    <div className="director-tournament-main">
                      <strong>{tournament.name || "Untitled Tournament"}</strong>
                      <span>{formatDateRange(tournament)}</span>
                    </div>
                    <div className="director-tournament-pills">
                      <div className="leaderboard-pill-row">
                        <span className={`status-pill status-${String(tournament.status || "draft")}`}>
                          {statusLabel(tournament.status)}
                        </span>
                        <span className="open-match-chip">{familyLabel(tournament.competitionFamily)}</span>
                        <span className="open-match-chip">{isPublicTournament(tournament) ? "Public" : "Private"}</span>
                      </div>
                    </div>
                    <div className="director-tournament-facts">
                      <span>{tournament.allowNativeMatches === false ? "Imported only" : "Native matches"}</span>
                      <span>{tournament.participantApprovalMode === "open" ? "Open access" : "Owner approval"}</span>
                    </div>
                    <div className="director-row-actions">
                      <Link to={tournamentWorkbenchPath(tournament.tournamentId)} className="match-search-button leaderboard-inline-link">
                        Workbench
                      </Link>
                      {isPublicTournament(tournament) ? (
                        <Link to={`${tournamentPublicPath(tournament.tournamentId)}/leaderboard`} className="detail-link">
                          Leaderboard
                        </Link>
                      ) : (
                        <span className="director-disabled-action">Private standings</span>
                      )}
                      {tournament.competitionFamily === "arc" && tournament.allowNativeMatches !== false && (
                        <Link
                          to={`/matches/manage/new?competition=nra_arc&tournamentId=${encodeURIComponent(tournament.tournamentId)}`}
                          className="registration-auth-secondary leaderboard-inline-link"
                        >
                          Add Match
                        </Link>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {organizerRows.length > 0 && (
            <section className="leaderboard-card tournament-display-section">
              <div className="tournament-section-header">
                <div>
                  <div className="owner-registration-section-label">Organizer</div>
                  <h2>Access requests</h2>
                </div>
              </div>
              <div className="director-match-row-list">
                {organizerRows.map((row) => {
                  const targetPath = row.status === "approved"
                    ? tournamentWorkbenchPath(row.tournamentId)
                    : tournamentPublicPath(row.tournamentId);

                  return (
                    <article key={`${row.tournamentId}:${row.participantUserId || row.displayName}`} className="director-match-row leaderboard-link-card">
                      {row.tournamentId && (
                        <Link
                          to={targetPath}
                          className="card-click-target"
                          aria-label={`Open ${row.displayName || "this tournament"}`}
                        />
                      )}
                      <div>
                        <div className="leaderboard-pill-row">
                          <span className={`status-pill status-${row.status}`}>{statusLabel(row.status)}</span>
                          {row.family && <span className="open-match-chip">{familyLabel(row.family)}</span>}
                          <span className="open-match-chip">{row.canLinkNativeMatches ? "Can link matches" : "Access only"}</span>
                        </div>
                        <strong>{row.displayName}</strong>
                        <span>{row.contactEmail || row.participantDisplayName || row.tournamentId}</span>
                      </div>
                      <div className="director-row-actions">
                        {row.tournamentId ? (
                          <Link to={targetPath} className="detail-link">
                            {row.status === "approved" ? "Workbench" : "Public Tournament"}
                          </Link>
                        ) : (
                          <span className="director-disabled-action">Missing tournament id</span>
                        )}
                        {row.status === "approved" && row.family === "arc" && row.canLinkNativeMatches && (
                          <Link
                            to={`/matches/manage/new?competition=nra_arc&tournamentId=${encodeURIComponent(row.tournamentId)}`}
                            className="detail-link"
                          >
                            Add Match
                          </Link>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}
    </section>
  );
}

export const DirectorTournaments_Page = {
  path: "/matches/manage/tournaments",
  element: <DirectorTournaments />
};
